import { z } from "zod";
import { Area, ROUND_SECONDS, Scope, type Grenade, type Side, type Stuff } from "./stuff.ts";
import { BOMB_SECONDS, Role, type Combo } from "./teamplay.ts";

/**
 * 0.8.0, executions as plans: up to five players, each with actions in order
 * (move, hold, throw a single, throw a combo, call). Times are worked out from
 * how far each player goes and how fast (their weapon, running, walking or
 * crouching), unless locked on the clock or tied to another player's action.
 *
 * Three kinds share it: an execute (T), a setup (CT holds, rotations, an info
 * play), a retake (CT, on the bomb timer, from a bomb placed on the map).
 */

// ---------------------------------------------------------------- kinds and clocks

export const EXECUTION_KINDS = ["execute", "setup", "retake"] as const;
export const ExecutionKind = z.enum(EXECUTION_KINDS);
export type ExecutionKind = z.infer<typeof ExecutionKind>;

/** The side that plays each kind. */
export const kindSide = (k: ExecutionKind): Side => (k === "execute" ? "t" : "ct");
/** The clock a kind runs on, counting down: the round (1:55) or the bomb (0:40). */
export const kindClock = (k: ExecutionKind): number => (k === "retake" ? BOMB_SECONDS : ROUND_SECONDS);

// ---------------------------------------------------------------- speeds

export const WEAPONS = ["knife", "pistol", "smg", "ak", "m4", "awp"] as const;
export const Weapon = z.enum(WEAPONS);
export type Weapon = z.infer<typeof Weapon>;

/** Running speed in units per second, with the weapon in hand (CS2). */
export const WEAPON_SPEED: Record<Weapon, number> = { knife: 250, pistol: 240, smg: 240, ak: 215, m4: 225, awp: 200 };

export const PACES = ["run", "walk", "crouch"] as const;
export const Pace = z.enum(PACES);
export type Pace = z.infer<typeof Pace>;
/** Walking (Shift) is about half the running speed, crouching about a third. */
export const PACE_FACTOR: Record<Pace, number> = { run: 1, walk: 0.52, crouch: 0.34 };

/** Seconds at a throw spot before the throw: lining it up; less for a jump throw. */
export const LINEUP_SECONDS = 1.5;
export const JUMP_LINEUP_SECONDS = 1;
/** Seconds between two throws of a combo. */
export const COMBO_GAP_SECONDS = 1.2;
/** Closer than this (world units) to a throw spot, a player is on it: no walk first. */
export const ON_SPOT_UNITS = 16;

/** Flashes and HEs go off this long after the throw. */
export const FUSE_SECONDS = 1.6;
/** How long each grenade's effect stays: a smoke about 18 s, fire about 7 s, a flash or HE a moment. */
export const EFFECT_SECONDS: Record<Grenade, number> = { smoke: 18, molotov: 7, flash: 0.5, he: 0.5 };

/** A smoke or molotov in the air: about this long for a short throw, longer when far, within bounds. */
export function flightSeconds(grenade: Grenade, from: { x: number; y: number }, to: { x: number; y: number }): number {
  if (grenade === "flash" || grenade === "he") return FUSE_SECONDS;
  const d = Math.hypot(to.x - from.x, to.y - from.y);
  return Math.min(3.5, Math.max(0.6, 0.4 + d / 800));
}

// ---------------------------------------------------------------- the plan

const coord = z.number().finite().min(-16384).max(16384);
export const PlanPoint = z.object({ x: coord, y: coord, z: coord.nullable().default(null) });
export type PlanPoint = z.infer<typeof PlanPoint>;
const Yaw = z.number().finite().min(-360).max(360);
const PlanStart = PlanPoint.extend({ yaw: Yaw.default(0) });
export type PlanStart = z.infer<typeof PlanStart>;
const PlanId = z.string().regex(/^[A-Za-z0-9_-]{1,24}$/, "Not an id");
const Uuid = z.string().uuid();
/** A time on the plan's clock (seconds, counting down). */
const ClockTime = z.number().finite().min(0).max(ROUND_SECONDS);

export const ACTION_EVENTS = ["start", "end", "lands"] as const;
export type ActionEvent = (typeof ACTION_EVENTS)[number];

/** "After Kroz's flash goes off, 0.5 s later": an action tied to another one, here or in another player's part. */
export const AfterAction = z.object({
  ref: PlanId,
  event: z.enum(ACTION_EVENTS).default("end"),
  delay: z.number().finite().min(-30).max(60).default(0),
});
export type AfterAction = z.infer<typeof AfterAction>;

const timing = {
  id: PlanId,
  /** Locked start on the clock; null = right after the action before (or `after`). */
  at: ClockTime.nullable().default(null),
  after: AfterAction.nullable().default(null),
};

export const MoveAction = z.object({ ...timing, kind: z.literal("move"), to: PlanPoint, pace: Pace.default("run") });
export const HoldAction = z.object({ ...timing, kind: z.literal("hold"), yaw: Yaw, seconds: z.number().finite().min(0.5).max(90).default(3) });
export const ThrowAction = z.object({
  ...timing,
  kind: z.literal("throw"),
  stuffId: Uuid,
  /** Seconds lining it up at the spot; null = 1.5, or 1 for a jump throw. */
  lineup: z.number().finite().min(0).max(10).nullable().default(null),
});
export const ComboAction = z.object({
  ...timing,
  kind: z.literal("combo"),
  comboId: Uuid,
  lineup: z.number().finite().min(0).max(10).nullable().default(null),
  gap: z.number().finite().min(0.3).max(10).default(COMBO_GAP_SECONDS),
});
export const CallAction = z.object({ ...timing, kind: z.literal("call"), text: z.string().trim().min(1, "Write the call").max(80) });

export const PlanAction = z.discriminatedUnion("kind", [MoveAction, HoldAction, ThrowAction, ComboAction, CallAction]);
export type PlanAction = z.infer<typeof PlanAction>;
export type PlanActionKind = PlanAction["kind"];
/** What opponents do: they move, hold and call, nothing else. */
export const OpponentAction = z.discriminatedUnion("kind", [MoveAction, HoldAction, CallAction]);

export const MAX_PLAN_PLAYERS = 5;
export const MAX_PLAN_OPPONENTS = 5;
export const MAX_PLAN_ACTIONS = 40;
export const MAX_PLAN_CHAPTERS = 8;

export const PlanPlayer = z.object({
  id: PlanId,
  slot: z.number().int().min(1).max(5),
  role: Role,
  /** The teammate who plays it: they get its throws to learn and a "my part" view. */
  memberId: Uuid.nullable().default(null),
  weapon: Weapon.default("ak"),
  start: PlanStart,
  actions: z.array(PlanAction).max(MAX_PLAN_ACTIONS).default([]),
});
export type PlanPlayer = z.infer<typeof PlanPlayer>;

export const PlanOpponent = z.object({
  id: PlanId,
  label: z.string().trim().max(30).default(""),
  weapon: Weapon.default("ak"),
  start: PlanStart,
  actions: z.array(OpponentAction).max(30).default([]),
});
export type PlanOpponent = z.infer<typeof PlanOpponent>;

export const PlanChapter = z.object({ id: PlanId, name: z.string().trim().min(1, "Name the chapter").max(30), at: ClockTime });
export type PlanChapter = z.infer<typeof PlanChapter>;

export const Plan = z
  .object({
    /** The part of the clock the plan covers, counting down: 1:20 → 0:55, or 0:40 → 0:00 for a retake. */
    window: z.object({ from: ClockTime, to: ClockTime }).refine((w) => w.from > w.to, { message: "The window starts before it ends: the clock counts down" }),
    /** A retake's bomb. */
    bomb: PlanPoint.nullable().default(null),
    /** When the CTs learn where it is (seen, or told); null = known from the start. */
    bombKnownAt: ClockTime.nullable().default(null),
    chapters: z.array(PlanChapter).max(MAX_PLAN_CHAPTERS).default([]),
    players: z.array(PlanPlayer).max(MAX_PLAN_PLAYERS).default([]),
    opponents: z.array(PlanOpponent).max(MAX_PLAN_OPPONENTS).default([]),
  })
  .superRefine((p, ctx) => {
    const ids = [...p.players, ...p.opponents].flatMap((a) => [a.id, ...a.actions.map((x) => x.id)]).concat(p.chapters.map((c) => c.id));
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "Two items of the plan have the same id" });
    const slots = p.players.map((x) => x.slot);
    if (new Set(slots).size !== slots.length) ctx.addIssue({ code: "custom", path: ["players"], message: "Two players have the same slot" });
    const pinned = p.players.map((x) => x.memberId).filter(Boolean);
    if (new Set(pinned).size !== pinned.length) ctx.addIssue({ code: "custom", path: ["players"], message: "A teammate is pinned to two players" });
    const actionIds = new Set([...p.players, ...p.opponents].flatMap((a) => a.actions.map((x) => x.id)));
    for (const a of [...p.players, ...p.opponents].flatMap((x) => x.actions)) {
      if (a.at !== null && a.after !== null) ctx.addIssue({ code: "custom", message: "An action has a locked time and a link to another: keep one" });
      if (a.after && (!actionIds.has(a.after.ref) || a.after.ref === a.id)) ctx.addIssue({ code: "custom", message: "An action is tied to one that is not in the plan" });
    }
  });
export type Plan = z.infer<typeof Plan>;
export type PlanInput = z.input<typeof Plan>;

const Title = z.string().trim().min(2, "At least 2 characters").max(60, "At most 60 characters");

export const ExecutionInput = z
  .object({
    map: z.string(),
    kind: ExecutionKind,
    target: Area,
    title: Title,
    notes: z.string().trim().max(1000).default(""),
    plan: Plan,
  })
  .superRefine((e, ctx) => {
    const max = kindClock(e.kind);
    if (e.plan.window.from > max) ctx.addIssue({ code: "custom", path: ["plan", "window"], message: e.kind === "retake" ? "A retake runs on the bomb timer: 0:40 at most" : "The round clock starts at 1:55" });
    if (e.kind === "retake" && !e.plan.bomb) ctx.addIssue({ code: "custom", path: ["plan", "bomb"], message: "Place the bomb for a retake" });
  });
export type ExecutionInput = z.input<typeof ExecutionInput>;

export const CreateExecutionInput = z.intersection(ExecutionInput, z.object({ scope: Scope }));
export type CreateExecutionInput = z.input<typeof CreateExecutionInput>;

export interface Execution {
  id: string;
  teamId: string | null;
  map: string;
  kind: ExecutionKind;
  side: Side;
  target: Area;
  title: string;
  notes: string;
  plan: Plan;
  /** The singles the plan throws, combos' throws included, as the library has them. */
  stuffs: Stuff[];
  combos: Combo[];
  /** Teammates pinned to players. */
  members: { id: string; name: string; avatarUrl: string | null }[];
  author: { id: string; name: string };
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Every single and combo a plan uses. */
export function planRefs(plan: Pick<Plan, "players">): { stuffIds: string[]; comboIds: string[] } {
  const actions = plan.players.flatMap((p) => p.actions);
  return {
    stuffIds: [...new Set(actions.flatMap((a) => (a.kind === "throw" ? [a.stuffId] : [])))],
    comboIds: [...new Set(actions.flatMap((a) => (a.kind === "combo" ? [a.comboId] : [])))],
  };
}

// ---------------------------------------------------------------- the schedule

/** What the schedule needs to know of a single: where it is thrown from, facing where, and where it lands. */
export interface PlanThrow {
  stuffId: string;
  title: string;
  grenade: Grenade;
  x: number;
  y: number;
  z: number | null;
  yaw: number;
  jump: boolean;
  landing: { x: number; y: number; z: number | null };
}

export interface PlanLookup {
  stuff(id: string): PlanThrow | null;
  /** A combo's throws in order. */
  combo(id: string): PlanThrow[] | null;
}

const throwOf = (s: Stuff): PlanThrow => ({
  stuffId: s.id,
  title: s.title,
  grenade: s.grenade,
  x: s.throw.x,
  y: s.throw.y,
  z: s.throw.z,
  yaw: s.throw.yaw,
  jump: s.technique.jump,
  landing: s.landing,
});

export function planLookup(stuffs: Stuff[], combos: Combo[]): PlanLookup {
  const singles = new Map(stuffs.map((s) => [s.id, throwOf(s)]));
  const sets = new Map(combos.map((c) => [c.id, c.steps.map((st) => throwOf(st.stuff))]));
  return { stuff: (id) => singles.get(id) ?? null, combo: (id) => sets.get(id) ?? null };
}

export type SegmentKind = "wait" | "move" | "travel" | "hold" | "lineup";

/** A stretch of a player's time: standing, walking somewhere, holding an angle, lining up a throw. */
export interface Segment {
  /** The action it belongs to; null for waiting before one. */
  action: string | null;
  kind: SegmentKind;
  /** Seconds since the window started. */
  start: number;
  end: number;
  from: PlanPoint;
  to: PlanPoint;
  yawFrom: number;
  yawTo: number;
  pace?: Pace;
}

export interface PlannedThrow {
  action: string;
  actor: string;
  /** Its place in a combo (0 for a single). */
  index: number;
  stuffId: string;
  title: string;
  grenade: Grenade;
  /** Seconds since the window started: thrown, lands (or goes off), and gone. */
  at: number;
  lands: number;
  gone: number;
  from: PlanPoint;
  to: PlanPoint;
}

export interface PlannedCall {
  action: string;
  actor: string;
  at: number;
  text: string;
}

export interface ActionTime {
  start: number;
  end: number;
  /** When its (last) grenade lands or goes off; the end for anything else. */
  lands: number;
}

export type WarningKind = "too_early" | "missing" | "loop";
export interface PlanWarning {
  action: string;
  kind: WarningKind;
  /** For too_early: the earliest time on the clock it can start. */
  earliest?: number;
}

export interface ScheduledActor {
  id: string;
  start: PlanStart;
  segments: Segment[];
  end: number;
}

export interface Schedule {
  /** Seconds the window lasts. */
  span: number;
  /** Seconds until the last thing is done (can be past the window). */
  duration: number;
  actors: Map<string, ScheduledActor>;
  times: Map<string, ActionTime>;
  throws: PlannedThrow[];
  calls: PlannedCall[];
  warnings: PlanWarning[];
  toClock: (elapsed: number) => number;
  toElapsed: (clock: number) => number;
}

const yawOf = (from: { x: number; y: number }, to: { x: number; y: number }, fallback: number) =>
  Math.hypot(to.x - from.x, to.y - from.y) < 1 ? fallback : (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;
const dist = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(b.x - a.x, b.y - a.y);
const pt = (p: { x: number; y: number; z?: number | null }): PlanPoint => ({ x: p.x, y: p.y, z: p.z ?? null });

interface Actor {
  id: string;
  weapon: Weapon;
  start: PlanStart;
  actions: PlanAction[];
}

function runActor(
  actor: Actor,
  lookup: PlanLookup,
  toElapsed: (clock: number) => number,
  known: Map<string, ActionTime>,
  out: { times: Map<string, ActionTime>; throws: PlannedThrow[]; calls: PlannedCall[]; warnings: PlanWarning[] },
): ScheduledActor {
  const speed = WEAPON_SPEED[actor.weapon];
  const segments: Segment[] = [];
  let pos: PlanPoint = pt(actor.start);
  let yaw = actor.start.yaw;
  let t = 0;
  const go = (to: PlanPoint, pace: Pace, kind: "move" | "travel", action: string) => {
    const d = dist(pos, to);
    const dur = d / (speed * PACE_FACTOR[pace]);
    const heading = yawOf(pos, to, yaw);
    segments.push({ action, kind, start: t, end: t + dur, from: pos, to, yawFrom: yaw, yawTo: heading, pace });
    t += dur;
    pos = to;
    yaw = heading;
  };
  for (const a of actor.actions) {
    const earliest = t;
    let start = earliest;
    if (a.at !== null) {
      const wanted = toElapsed(a.at);
      if (wanted < earliest - 0.05) out.warnings.push({ action: a.id, kind: "too_early", earliest: earliest });
      else start = wanted;
    } else if (a.after) {
      const ref = known.get(a.after.ref);
      if (ref) start = Math.max(earliest, ref[a.after.event] + a.after.delay);
    }
    if (start > t) {
      segments.push({ action: null, kind: "wait", start: t, end: start, from: pos, to: pos, yawFrom: yaw, yawTo: yaw });
      t = start;
    }
    let lands = 0;
    switch (a.kind) {
      case "move":
        go(pt(a.to), a.pace, "move", a.id);
        break;
      case "hold":
        segments.push({ action: a.id, kind: "hold", start: t, end: t + a.seconds, from: pos, to: pos, yawFrom: yaw, yawTo: a.yaw });
        t += a.seconds;
        yaw = a.yaw;
        break;
      case "call":
        out.calls.push({ action: a.id, actor: actor.id, at: t, text: a.text });
        break;
      case "throw":
      case "combo": {
        const list = a.kind === "throw" ? (lookup.stuff(a.stuffId) ? [lookup.stuff(a.stuffId)!] : null) : lookup.combo(a.comboId);
        if (!list || list.length === 0) {
          out.warnings.push({ action: a.id, kind: "missing" });
          break;
        }
        list.forEach((th, i) => {
          const spot = pt(th);
          if (dist(pos, spot) > ON_SPOT_UNITS) go(spot, "run", "travel", a.id);
          const wait = i === 0 || a.kind === "throw" ? (a.lineup ?? (th.jump ? JUMP_LINEUP_SECONDS : LINEUP_SECONDS)) : a.gap;
          segments.push({ action: a.id, kind: "lineup", start: t, end: t + wait, from: pos, to: pos, yawFrom: yaw, yawTo: th.yaw });
          t += wait;
          yaw = th.yaw;
          const flight = flightSeconds(th.grenade, th, th.landing);
          out.throws.push({
            action: a.id,
            actor: actor.id,
            index: i,
            stuffId: th.stuffId,
            title: th.title,
            grenade: th.grenade,
            at: t,
            lands: t + flight,
            gone: t + flight + EFFECT_SECONDS[th.grenade],
            from: spot,
            to: pt(th.landing),
          });
          lands = t + flight;
        });
        break;
      }
    }
    out.times.set(a.id, { start, end: t, lands: Math.max(lands, t) });
  }
  return { id: actor.id, start: actor.start, segments, end: t };
}

/**
 * Works out the whole plan: each player's (and opponent's) stretches of time,
 * every throw with when it lands and how long it lasts, the calls, and what does
 * not add up (a locked time too early, a throw no longer in the library, links
 * that wait on each other).
 */
export function schedulePlan(plan: Pick<Plan, "window" | "players" | "opponents">, lookup: PlanLookup): Schedule {
  const span = plan.window.from - plan.window.to;
  const toElapsed = (clock: number) => plan.window.from - clock;
  const toClock = (elapsed: number) => plan.window.from - elapsed;
  const actors: Actor[] = [...plan.players, ...plan.opponents];
  let known = new Map<string, ActionTime>();
  type Out = { times: Map<string, ActionTime>; throws: PlannedThrow[]; calls: PlannedCall[]; warnings: PlanWarning[] };
  const empty = (): Out => ({ times: new Map(), throws: [], calls: [], warnings: [] });
  let result: { actors: ScheduledActor[]; out: Out } = { actors: [], out: empty() };
  // Links to other actions settle in a few passes; still moving after many means a loop.
  const passes = actors.reduce((n, a) => n + a.actions.filter((x) => x.after).length, 0) + 2;
  let stable = false;
  for (let i = 0; i < passes && !stable; i++) {
    const out = empty();
    const scheduled = actors.map((a) => runActor(a, lookup, toElapsed, known, out));
    stable = [...out.times].every(([id, v]) => {
      const before = known.get(id);
      return before && Math.abs(before.start - v.start) < 1e-6 && Math.abs(before.end - v.end) < 1e-6;
    });
    known = out.times;
    result = { actors: scheduled, out };
  }
  const { actors: done, out } = result;
  if (!stable) for (const a of actors.flatMap((x) => x.actions)) if (a.after) out.warnings.push({ action: a.id, kind: "loop" });
  // Earliest times given back on the clock.
  for (const w of out.warnings) if (w.earliest !== undefined) w.earliest = toClock(w.earliest);
  const duration = Math.max(0, ...done.map((a) => a.end), ...out.throws.map((th) => th.lands));
  return {
    span,
    duration,
    actors: new Map(done.map((a) => [a.id, a])),
    times: out.times,
    throws: out.throws.sort((a, b) => a.at - b.at),
    calls: out.calls.sort((a, b) => a.at - b.at),
    warnings: out.warnings,
    toClock,
    toElapsed,
  };
}

/** The shortest turn from one yaw to another, in degrees (−180 to 180). */
export const turn = (from: number, to: number) => ((((to - from) % 360) + 540) % 360) - 180;
/** How long a player takes to turn to a new direction, in seconds. */
const TURN_SECONDS = 0.25;

export interface ActorState {
  x: number;
  y: number;
  z: number | null;
  yaw: number;
  moving: boolean;
  segment: Segment | null;
}

/** Where a player is at a moment, facing where: walked along their stretches, turning smoothly. */
export function actorAt(actor: ScheduledActor, elapsed: number): ActorState {
  const segs = actor.segments;
  if (segs.length === 0 || elapsed <= segs[0].start) return { ...pt(actor.start), yaw: actor.start.yaw, moving: false, segment: null };
  const s = segs.find((x) => elapsed >= x.start && elapsed < x.end) ?? null;
  if (!s) {
    const last = segs[segs.length - 1];
    return { ...last.to, yaw: last.yawTo, moving: false, segment: null };
  }
  const len = s.end - s.start || 1;
  const k = Math.min(1, Math.max(0, (elapsed - s.start) / len));
  const turning = Math.min(1, (elapsed - s.start) / TURN_SECONDS);
  return {
    x: s.from.x + (s.to.x - s.from.x) * k,
    y: s.from.y + (s.to.y - s.from.y) * k,
    z: s.to.z ?? s.from.z,
    yaw: s.yawFrom + turn(s.yawFrom, s.yawTo) * turning,
    moving: s.kind === "move" || s.kind === "travel",
    segment: s,
  };
}

/** Every spot a plan touches, for zooming the map on it. */
export function planPoints(plan: Pick<Plan, "players" | "opponents" | "bomb">, lookup: PlanLookup): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (const a of [...plan.players, ...plan.opponents]) {
    out.push(a.start);
    for (const x of a.actions as PlanAction[]) {
      if (x.kind === "move") out.push(x.to);
      if (x.kind === "throw") {
        const th = lookup.stuff(x.stuffId);
        if (th) out.push(th, th.landing);
      }
      if (x.kind === "combo") for (const th of lookup.combo(x.comboId) ?? []) out.push(th, th.landing);
    }
  }
  if (plan.bomb) out.push(plan.bomb);
  return out;
}

/** Seconds as a clock, to the tenth when it matters: 1:12, 0:05.5. */
export function planClock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds * 10) / 10);
  const whole = Math.floor(s);
  const tenth = Math.round((s - whole) * 10);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}${tenth ? `.${tenth}` : ""}`;
}
